/**
 * Tabs instead of subpages ("New navigation", v3 mockups): a sidebar entry opens a
 * page whose related pages are tabs across its top, drawn like the FinOps view tabs
 * (`.aa-tabstrip`). Every tab is an existing route, so each keeps its own URL and
 * deep links; PillarTabBar draws the strip above any page whose URL is in a group.
 *
 * Tab: { key, to, labelKey (namespaced), icon, match?, when? }
 *   match  extra paths that light the tab (detail pages under it); `to`'s path
 *          always matches, and so does anything under it unless `exact`.
 *   query  { name: value } the URL must carry (a view of the same route); a tab
 *          with no `query` matches only when no other tab's query does.
 *   when   gate: 'admin' | 'billing' | { feature } | { orgIds: [] }
 */

const P = (key) => `nav:pillarTabs.${key}`;
const I = (key) => `nav:pillarItems.${key}`;

export const TAB_GROUPS = Object.freeze({
  inbox: [
    { key: 'inbox', to: '/agent-atlas/inbox', labelKey: I('inbox'), icon: 'inbox', when: { feature: 'RECOMMENDATIONS' } },
    { key: 'rules', to: '/admin/recommendation-rules', labelKey: P('rules'), icon: 'target', when: ['admin', { orgIds: [3] }] },
  ],
  notifications: [
    { key: 'notifications', to: '/agent-atlas/alerts', labelKey: I('notifications'), icon: 'bell' },
    { key: 'routing', to: '/admin/alert-configuration', labelKey: P('routing'), icon: 'git-merge', when: 'admin' },
  ],
  estate: [
    // The cross-source lists (user, 2026-10-27): Cloud is its own Discovery entry and the code
    // repositories a Code Security tab.
    { key: 'agents', to: '/agent-atlas/inventory', labelKey: P('agentsApps'), icon: 'cpu' },
    // a view of the AI-BOM route: every MCP server, skill, plugin and tool in one list
    { key: 'mcpSkillsTools', to: '/security-atlas/ai-bom?view=mcp-skills-tools', query: { view: 'mcp-skills-tools' }, labelKey: P('mcpSkillsTools'), icon: 'server' },
  ],
  // Discovery › Cloud (2026-10-27): the Cloud Discovery module's discovery pages. Its Agent Registry
  // is left out: AI Estate › Agents & apps lists cloud agents with every other source's.
  cloud: [
    { key: 'cloud', to: '/cloud/overview', labelKey: P('cloud'), icon: 'cloud' },
    { key: 'resources', to: '/cloud/inventory', labelKey: P('cloudResources'), icon: 'database' },
    { key: 'identities', to: '/cloud/identities', labelKey: 'nav:items.identities', icon: 'key' },
    { key: 'knowledgeBases', to: '/cloud/knowledge-bases', labelKey: 'nav:items.knowledgeBases', icon: 'book-open' },
  ],
  // Discovery › AI Bill of Materials (2026-10-27): its own entry, not an AI Estate tab, since it will
  // hold devices too. One tab, so no strip; the group keeps the entry lit on every ?type= / ?asset=
  // view, while ?view=mcp-skills-tools stays AI Estate's tab (a query tab wins, see tabFor).
  aiBom: [
    { key: 'aiBom', to: '/security-atlas/ai-bom', labelKey: I('aiBom'), icon: 'layers' },
  ],
  // Discovery › Endpoints (2026-10-27): the Endpoint Security dashboard, its devices and the AI
  // tools it links to (Devices and Endpoint AI tools were AI Estate tabs).
  endpoint: [
    { key: 'dashboard', to: '/endpoint-security', labelKey: 'nav:items.endpoints', icon: 'eye', exact: true },
    { key: 'devices', to: '/endpoint-security/endpoints', labelKey: P('devices'), icon: 'monitor' },
    { key: 'tools', to: '/endpoint-security/tool-catalog', labelKey: P('endpointTools'), icon: 'package' },
  ],
  redTeam: [
    { key: 'scans', to: '/agent-atlas/assessment/red-team', labelKey: P('scans'), icon: 'shield' },
    { key: 'schedules', to: '/agent-atlas/assessment/schedules', labelKey: I('scheduledRuns'), icon: 'clock' },
  ],
  codeSecurity: [
    { key: 'overview', to: '/sast/overview', labelKey: P('overview'), icon: 'grid' },
    { key: 'findings', to: '/sast/findings', labelKey: I('findings'), icon: 'alert-circle', match: ['/sast/scans'] },
    // the repositories connected for scanning (an AI Estate tab until 2026-10-27)
    { key: 'repositories', to: '/sast/repositories', labelKey: 'nav:items.repositories', icon: 'git-branch' },
  ],
  detections: [
    { key: 'signals', to: '/security-atlas/signals', labelKey: P('signals'), icon: 'activity' },
    { key: 'sensitiveData', to: '/security-atlas/data-security', labelKey: I('sensitiveData'), icon: 'database' },
    { key: 'deepfake', to: '/security-atlas/deepfake-detector', labelKey: I('deepfake'), icon: 'eye' },
  ],
  incidents: [
    { key: 'incidents', to: '/security-atlas/incidents', labelKey: I('incidents'), icon: 'alert-triangle' },
    { key: 'response', to: '/security-atlas/response', labelKey: I('responseApprovals'), icon: 'zap' },
    { key: 'tuning', to: '/security-atlas/tuning', labelKey: I('mutesTuning'), icon: 'volume-x' },
    { key: 'approvals', to: '/security-atlas/automation', labelKey: P('approvalSettings'), icon: 'sliders', when: 'admin' },
  ],
  // FinOps' views (they drew their own strip under the page title; now the shared one over it).
  finops: [
    { key: 'tokenomics', to: '/agent-atlas/finops/tokenomics', labelKey: P('finopsTokenomics'), icon: 'dollar-sign', match: ['/agent-atlas/finops'], exact: true },
    { key: 'usageRoi', to: '/agent-atlas/finops/usage-roi', labelKey: P('finopsUsageRoi'), icon: 'trending-up' },
    { key: 'licensing', to: '/agent-atlas/finops/licensing', labelKey: P('finopsLicensing'), icon: 'credit-card' },
    { key: 'budgets', to: '/agent-atlas/finops/budget', labelKey: P('finopsBudgets'), icon: 'pie-chart' },
    { key: 'settings', to: '/agent-atlas/finops/settings', labelKey: P('finopsSettings'), icon: 'settings' },
  ],
  adoption: [
    { key: 'conversations', to: '/agent-atlas/adoption/conversations', labelKey: P('conversations'), icon: 'message-square' },
    { key: 'unanswered', to: '/agent-atlas/adoption/knowledge-gaps', labelKey: I('unansweredQuestions'), icon: 'help-circle' },
    { key: 'topics', to: '/agent-atlas/adoption/intent-analysis', labelKey: I('topicsIntents'), icon: 'target' },
    { key: 'sources', to: '/agent-atlas/adoption/sources', labelKey: P('sourceIntelligence'), icon: 'database' },
  ],
  // Governance › Policy and Guardrails: guardrails are enforcement rules beside the policies.
  // Guardrails keeps its route (/admin/custom-guardrails) and its gate: the route's
  // requiredModule="security" (browse-but-locked: the page shows ModuleLocked, never
  // hidden, as in the classic sidebar), so the tab carries no `when`. The Policy page's
  // own Violations | Policies tabs (?tab=) sit under this strip, inside the page.
  policy: [
    { key: 'policy', to: '/agent-atlas/policy', labelKey: I('policyRules'), icon: 'lock' },
    { key: 'guardrails', to: '/admin/custom-guardrails', labelKey: P('guardrails'), icon: 'sliders' },
  ],
  settings: [
    { key: 'users', to: '/admin/users', labelKey: P('users'), icon: 'users', when: 'admin' },
    { key: 'teams', to: '/admin/teams', labelKey: P('teams'), icon: 'briefcase', when: 'admin' },
    { key: 'roles', to: '/admin/custom-roles', labelKey: P('roles'), icon: 'shield', when: 'admin' },
    { key: 'sso', to: '/admin/sso', labelKey: P('sso'), icon: 'key', when: 'admin' },
    { key: 'apiKeys', to: '/admin/api-keys', labelKey: P('apiKeys'), icon: 'lock', when: 'admin' },
    { key: 'integrations', to: '/admin/integrations', labelKey: P('integrations'), icon: 'link', when: 'admin' },
    { key: 'data', to: '/admin/data-policy', labelKey: P('dataPrivacy'), icon: 'lock', when: 'admin' },
    { key: 'ai', to: '/admin/ai-provider', labelKey: P('aiProvider'), icon: 'cpu', when: 'admin' },
    { key: 'audit', to: '/admin/audit-log', labelKey: P('auditTrail'), icon: 'file-text', when: 'admin' },
    { key: 'billing', to: '/billing', labelKey: P('billing'), icon: 'credit-card', when: 'billing' },
  ],
  profile: [
    { key: 'profile', to: '/profile', labelKey: P('profile'), icon: 'user', exact: true },
    { key: 'security', to: '/profile/security', labelKey: P('signIn'), icon: 'lock' },
  ],
});

/** Groups whose page already draws these tabs itself (Profile: /profile/*), so no strip. */
export const OWN_TAB_STRIP = new Set(['profile']);

/** URL filters a group's tabs carry from one tab to the next (the views share one filter bar). */
export const TAB_CARRY = Object.freeze({ finops: ['surface', 'user', 'agent'] });

const pathOf = (to) => String(to).split('?')[0];
const under = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`);

/** Whether `tab` passes its gate for `ctx` ({ isAdmin, canBill, orgId, featureOn }). */
export const tabShown = (tab, ctx) => [].concat(tab.when || []).every((g) => {
  if (g === 'admin') return ctx.isAdmin;
  if (g === 'billing') return ctx.canBill;
  if (g.feature) return ctx.featureOn(g.feature);
  if (g.orgIds) return g.orgIds.includes(ctx.orgId);
  return true;
});

const tabMatches = (tab, pathname, params) => {
  if (tab.query && !Object.entries(tab.query).every(([k, v]) => params.get(k) === v)) return false;
  const paths = [pathOf(tab.to), ...(tab.match || [])];
  return paths.some((p) => (tab.exact ? pathname === p || pathname === `${p}/` : under(pathname, p)));
};

/**
 * The group and tab a URL is in, or null. A tab with a `query` wins over one
 * without on the same path, so /agent-atlas/assessment?view=overview is Overview.
 */
export const tabFor = (pathname, search = '') => {
  const params = new URLSearchParams(search);
  for (const [group, tabs] of Object.entries(TAB_GROUPS)) {
    const withQuery = tabs.find((t) => t.query && tabMatches(t, pathname, params));
    if (withQuery) return { group, tab: withQuery.key };
    const plain = tabs.find((t) => !t.query && tabMatches(t, pathname, params));
    if (plain) return { group, tab: plain.key };
  }
  return null;
};

/** Every route a group holds (for a sidebar entry's active state). */
export const groupRoutes = (group) => (TAB_GROUPS[group] || []).flatMap((t) => [pathOf(t.to), ...(t.match || [])]);
